// src/components/modals/RestoreItemsModal.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useAuth } from '../../AuthContext';

export default function RestoreItemsModal({
  isOpen,
  onClose,
  selectedSaleId,
  clientId,
  onRestoreSuccess, // callback to trigger reload in SalesInv
  formatUSD,
  toIntOr,
  setUiMessage,
}) {
  const { fetchWrapper } = useAuth();
  const [restoreInventory, setRestoreInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const gridRef = useRef(null);
  const [selectedRows, setSelectedRows] = useState([]);

  const columns = [
   {
    headerName: '',
    width: 50,
    pinned: 'left',
    suppressMenu: true,
    sortable: false,
    filter: false,
    lockPosition: 'left',
    checkboxSelection: true,           // ← old way — works in Community
    headerCheckboxSelection: true,     // ← "select all"
    headerCheckboxSelectionFilteredOnly: true, // optional: only selects filtered
  },
    { field: 'url', headerName: 'Thumb', width: 120, cellRenderer: (p) => p.value && p.value !== 'N/A' ? <img src={p.value} alt="" style={{width:100,height:'auto'}} /> : 'N/A' },
    { field: 'title', headerName: 'Title', flex: 1, minWidth: 220 },
    { field: 'section_name', headerName: 'Section', width: 130 },
    { field: 'per_item_cost_estimate', headerName: 'Price', width: 110, valueFormatter: (p) => formatUSD(p.value) },
    { field: 'quantity_estimate', headerName: 'Qty', width: 80, valueFormatter: (p) => String(toIntOr(p.value, 1)) },
    { field: 'status', headerName: 'Status', width: 110, valueFormatter: (p) => (p.value || '').toUpperCase() },
  ];
const loadItems = useCallback(async () => {
  if (!selectedSaleId) {
    setRestoreInventory([]);
    return;
  }

  setLoading(true);
  try {
    const body = {
      p_sale_id: selectedSaleId,
      p_client_id: clientId,
      p_section_id: null,          // all sections
      p_statuses: ['on hold'],     // only on hold items
    };

    const resp = await fetchWrapper(
      `${process.env.REACT_APP_API_URL}/supabase/rpc/select_inventory_items_by_statuses`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }
    );

    if (!resp.ok) {
      const errData = await resp.json().catch(() => ({}));
      throw new Error(errData.error?.message || 'Failed to load on hold items');
    }

    const data = await resp.json();

    // Map directly — no extra filter needed
    const mapped = data.map(item => ({
      id: item.id || '',
      url: item.signed_url || 'N/A',
      title: item.title || 'N/A',
      section_name: item.section_name || 'N/A',
      per_item_cost_estimate: item.per_item_cost_estimate,
      quantity_estimate: toIntOr(item.quantity_estimate, 1),
      status: item.status || '',
    }));

    setRestoreInventory(mapped);
  } catch (error) {
    console.error('Error loading on hold items:', error);
    setRestoreInventory([]);
    // Optional: pass setUiMessage as prop if you want error feedback
  } finally {
    setLoading(false);
  }
}, [selectedSaleId, clientId, fetchWrapper, toIntOr]);
  useEffect(() => {
  if (isOpen && selectedSaleId) {
    loadItems();
  }
}, [isOpen, selectedSaleId, loadItems]);

const handleRestore = async () => {
  if (selectedRows.length === 0) {
    // optional: setUiMessage('No items selected');
    return;
  }

  setLoading(true);
  try {
    await Promise.all(
      selectedRows.map(async (row) => {
        const response = await fetchWrapper(
          `${process.env.REACT_APP_API_URL}/supabase/rpc/update_inventory_item_status`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              p_id: row.id,
              p_sale_id: selectedSaleId,
              p_client_id: clientId,
              p_status: 'active', // restore to active
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error?.message || `Failed to restore item ${row.id}`
          );
        }
      })
    );

    // Success
    setUiMessage(`Restored ${selectedRows.length} items to ACTIVE`);
    onRestoreSuccess?.(); // triggers reload in SalesInv
    onClose();
  } catch (error) {
    console.error('Restore error:', error);
    setUiMessage(`Error restoring items: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold">Restore Items On Hold</h2>
          <button onClick={onClose} className="text-3xl">&times;</button>
        </div>

        <div className="p-4 flex-1 overflow-hidden">
          {loading && <p className="text-center">Loading...</p>}
          {restoreInventory.length === 0 && !loading && <p className="text-center text-gray-500">No items on hold</p>}
          <div className="ag-theme-quartz" style={{ height: '500px' }}>
            <AgGridReact
                ref={gridRef}
                rowData={restoreInventory}
                columnDefs={columns}
                rowSelection="multiple"  // ← simple old prop for multi-select
                onSelectionChanged={(event) => {
                setSelectedRows(event.api.getSelectedRows());
                }}
                getRowId={(p) => p.data.id}
                headerHeight={34}
                rowHeight={60}
                suppressPaginationPanel={false}
            />
          </div>
        </div>

        <div className="p-4 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border rounded">Cancel</button>
       <button
  onClick={handleRestore}
  disabled={loading || selectedRows.length === 0}
  className="px-6 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
>
  Restore Selected ({selectedRows.length})
</button>
        </div>
      </div>
    </div>
  );
}